/**
 * Human-friendly relative timestamps (KQA-10).
 *
 * Replaces stale hardcoded "2024-01-28" style strings with a live "x minutes/
 * hours/days ago" derived from a real record's timestamp. Returns a fallback
 * for null/invalid input so callers never render a raw ISO string or "Invalid".
 */
export function relativeTime(
  value: string | number | Date | null | undefined,
  fallback = 'Never'
): string {
  if (value == null || value === '') return fallback
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return fallback

  const diffMs = Date.now() - d.getTime()
  const future = diffMs < 0
  const abs = Math.abs(diffMs)

  const sec = Math.floor(abs / 1000)
  const min = Math.floor(sec / 60)
  const hr = Math.floor(min / 60)
  const day = Math.floor(hr / 24)
  const month = Math.floor(day / 30)
  const year = Math.floor(day / 365)

  const phrase = (n: number, unit: string) =>
    future ? `in ${n} ${unit}${n === 1 ? '' : 's'}` : `${n} ${unit}${n === 1 ? '' : 's'} ago`

  if (sec < 45) return future ? 'in a moment' : 'just now'
  if (min < 60) return phrase(min, 'minute')
  if (hr < 24) return phrase(hr, 'hour')
  if (day < 30) return phrase(day, 'day')
  if (month < 12) return phrase(month, 'month')
  return phrase(year, 'year')
}
